import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sfc/ui";
import { type RoundAggregate } from "@engine/round/aggregate";
import {
  getActiveProposals,
  getRecommenderFromUser,
  roundUsesTracks,
} from "@engine/round/accessors";
import {
  ALL_STATUSES,
  ALL_TRACK_CATEGORIES,
  isRoundObserver,
} from "@client/hooks/use-my-evaluations-data";
import { ProposalsProgressCard } from "@client/components/proposals-progress-card";
import { ProposalsStatusSummaryCards } from "@client/components/proposals-status-summary-cards";
import { ProposalsFilters } from "@client/components/proposals-filters";
import { ProposalsTableHeader } from "@client/components/proposals-table-header";
import { RoundPage } from "@client/components/utils/RoundPage";

const noop = () => {};

/** Match the actual round and viewer; never invent recommender controls. */
export function ProposalsLoadingSkeleton({
  round,
  userId,
}: {
  round: RoundAggregate;
  userId: string;
}) {
  const isRecommender = getRecommenderFromUser(round, userId) != null;
  const isObserver = isRoundObserver(round, userId);
  const rowCount = Math.min(
    8,
    getActiveProposals(round).filter(
      (proposal) => proposal.id !== round.holdProposalId,
    ).length,
  );
  return (
    <div
      className="min-h-full w-full"
      role="status"
      aria-label="Loading proposals"
      aria-busy="true"
    >
      <div inert aria-hidden="true">
        <RoundPage
          title="Proposals"
          description={
            round.isSfcRound ? undefined : "List of proposals for this round"
          }
          className="flex flex-col gap-6"
          cta={
            <div className="flex flex-wrap gap-2">
              {!round.isSfcRound && (
                <Skeleton className="h-9 w-60 rounded-md motion-reduce:animate-none" />
              )}
              <Skeleton className="h-9 w-48 rounded-md motion-reduce:animate-none" />
            </div>
          }
        >
          {round.isSfcRound && (
            <>
              {isRecommender && (
                <div className="dark flex flex-col gap-3">
                  <ProposalsProgressCard completed={null} total={null} />
                  <ProposalsStatusSummaryCards
                    counts={{
                      allActive: null,
                      allInactive: null,
                      hidden: null,
                      notStarted: null,
                      evaluated: null,
                      canceled: null,
                      recused: null,
                      deferred: null,
                      total: null,
                    }}
                    statusFilter={ALL_STATUSES}
                    showHidden={false}
                    onStatusFilterChange={noop}
                    onShowHiddenChange={noop}
                  />
                </div>
              )}
              <ProposalsFilters
                isRecommender={isRecommender}
                isObserver={isObserver}
                sort={isRecommender ? "status" : "org-name"}
                onSortChange={noop}
                trackFilter={ALL_TRACK_CATEGORIES}
                onTrackFilterChange={noop}
                statusFilter={ALL_STATUSES}
                onStatusFilterChange={noop}
                showHidden={false}
                onShowHiddenChange={noop}
                searchQuery=""
                searchActive={false}
                onSearchChange={noop}
                hasActiveFilters={false}
                onClearFilters={noop}
              />
            </>
          )}
          <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface-base">
            <Table containerClassName="bg-surface-base">
              {round.isSfcRound ? (
                <ProposalsTableHeader
                  isRecommender={isRecommender}
                  isObserver={isObserver}
                  showTracks={roundUsesTracks(round)}
                  sort={isRecommender ? "status" : "org-name"}
                  sortDirection={isRecommender ? "desc" : "asc"}
                  onSortChange={noop}
                />
              ) : (
                <TableHeader>
                  <TableRow>
                    {["Reviewed", "Title", "Request Amount"].map((label) => (
                      <TableHead key={label}>{label}</TableHead>
                    ))}
                    <TableHead aria-label="Actions" />
                  </TableRow>
                </TableHeader>
              )}
              <TableBody>
                {Array.from({ length: rowCount }, (_, i) => (
                  <TableRow key={i}>
                    {round.isSfcRound ? (
                      <TableCell
                        colSpan={
                          2 +
                          Number(roundUsesTracks(round)) +
                          Number(isObserver) +
                          (isRecommender ? 5 : 0)
                        }
                        className="px-4 py-3"
                      >
                        <Skeleton className="h-5 w-full max-w-md motion-reduce:animate-none" />
                      </TableCell>
                    ) : (
                      <>
                        {["w-32", "w-40", "w-20", "w-16"].map((width) => (
                          <TableCell key={width} className="py-3">
                            <Skeleton
                              className={`h-5 ${width} motion-reduce:animate-none`}
                            />
                          </TableCell>
                        ))}
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </RoundPage>
      </div>
    </div>
  );
}
